<script setup lang="ts">
import { mdiMenuDown } from "@mdi/js";
import PrimevueDropdown from "primevue/dropdown";
import Icon from "../Icon.vue";

interface Props {
	disabled?: boolean;
	readonly?: boolean;
	label: string;
	noFloatingLabel?: boolean;
	class?: string | string[];
	modelValue: string;
	size?: "small" | "medium" | "large";
	appendTo?: string;
	error?: string | null | undefined | Ref<string>;
	rootClass?: string | Record<string, boolean> | (string | Record<string, boolean>)[];
	labelClass?: string | Record<string, boolean> | (string | Record<string, boolean>)[];
	inputClass?: string | Record<string, boolean> | (string | Record<string, boolean>)[];
	itemClass?: string | Record<string, boolean> | (string | Record<string, boolean>)[];
	customElement?: boolean;
	options: { name: any; value: any; icon?: string; image?: string }[];
	variant?: "navigation";
}

const props = withDefaults(defineProps<Props>(), {
	class: "",
	disabled: false,
	label: "",
	modelValue: "",
	noFloatingLabel: false,
	customElement: false,
	size: "large",
	error: null,
	rootClass: "",
	labelClass: "",
	inputClass: "",
});
const emit = defineEmits(["update:modelValue", "blur", "change"]);
const focused = ref(false);

const value = computed({
	get() {
		return props.modelValue;
	},
	set(val) {
		emit("update:modelValue", val);
	},
});

const selectedValue = computed(() => {
	return props.options.filter((x) => x.value === value.value)[0];
});
</script>

<template>
	<div :class="['relative', !noFloatingLabel && 'mt-1.5', props.class || 'w-full']">
		<PrimevueDropdown
			:disabled="disabled || readonly"
			v-model="value"
			:options="options"
			optionLabel="name"
			optionValue="value"
			:placeholder="noFloatingLabel ? label : ''"
			selectionMessage=" "
			emptySelectionMessage=""
			emptyMessage="No Options"
			:appendTo="appendTo ? appendTo : 'self'"
			@focus="focused = true"
			@blur="
				() => {
					focused = false;
					$emit('blur');
				}
			"
			@change="$emit('change')"
			:pt="{
				root: {
					class: !customElement
						? [
								{ 'h-10': size === 'large', 'h-9': size === 'medium', 'h-6': size === 'small' },
								variant === 'navigation'
									? 'text-light-50 dark:text-light-50 bg-dark-700 dark:bg-dark-700'
									: 'text-dark-800 dark:text-light-50 bg-light-50 dark:bg-dark-800 border',
								'w-full rounded appearance-none transition-all  bg-[transparent] outline-none pl-4 pr-8 cursor-pointer flex items-center  rounded',
								{ 'border-error-500': error, 'border-light-500 dark:border-dark-600': !error && !focused, 'border-identity': focused },
								inputClass,
						  ]
						: [],
				},
				panel: {
					class: '!z-20',
				},
				input: {
					class: 'outline-none',
				},
				wrapper: {
					class: [
						variant === 'navigation' ? 'bg-dark-700' : 'bg-light-50 dark:bg-dark-800',
						'rounded  overflow-hidden border-x border-b border-light-500 dark:border-dark-600 mt-[1px]',
					],
				},
				item: {
					class: ['flex items-center border-t border-light-500 dark:border-dark-600 ', itemClass],
				},
			}"
		>
			<template #dropdownicon>
				<div v-if="customElement" />
				<slot name="icon" v-else>
					<div
						:class="[
							'absolute top-0 flex items-center justify-center w-6 pointer-events-none right-2',
							{ 'h-10': size === 'large', 'h-8': size === 'medium', 'h-6': size === 'small' },
						]"
					>
						<Icon
							class="absolute w-6 h-6"
							:class="variant === 'navigation' ? 'fill-light-50' : 'fill-dark-800 dark:fill-light-50'"
							:path="mdiMenuDown"
						/>
					</div>
				</slot>
			</template>
			<template v-if="selectedValue && customElement" #value="slotProps">
				<slot />
			</template>
			<template v-if="selectedValue && !customElement" #value="slotProps">
				<slot v-if="$slots.default" />
				<div
					v-else-if="selectedValue"
					class="absolute top-0 left-0 flex items-center justify-start w-full h-full gap-2 pl-4 pr-[30px] align-items-center"
				>
					<Icon :path="selectedValue.icon" v-if="selectedValue.icon" class="w-6 h-6" />
					<img v-else-if="selectedValue.image" :src="selectedValue.image" class="w-6 h-6" :alt="selectedValue.name" />
					<span class="overflow-hidden text-ellipsis whitespace-nowrap">{{ selectedValue.name }}</span>
				</div>
			</template>
			<template #option="slotProps">
				<div
					:class="[
						'custom-dropdown-item flex items-center w-full h-10 gap-2 px-4 transition-all text-xs font-bold',
						selectedValue && slotProps.option.value === selectedValue.value
							? 'bg-identity-50 text-identity-900 dark:bg-dark-700 dark:text-identity-300'
							: 'hover:bg-light-300 dark:hover:bg-dark-700',
					]"
				>
					<slot name="image" :icon="slotProps.option.icon ? slotProps.option.icon : slotProps.option.image">
						<DataFreeGuard>
							<Icon :path="slotProps.option.icon" v-if="slotProps.option.icon" class="w-6 h-6" />
							<img v-else-if="slotProps.option.image" :src="slotProps.option.image" class="w-6 h-6" :alt="slotProps.option.name" />
						</DataFreeGuard>
					</slot>
					{{ slotProps.option.name }}
				</div>
			</template>
		</PrimevueDropdown>
		<div
			v-if="!noFloatingLabel"
			:class="['absolute top-0 left-2 flex items-center pointer-events-none transition-all', { 'top-[-12px] h-auto': value, 'h-full': !value }]"
		>
			<label
				:class="[
					'px-2 text-base transition-all before:bg-light-50 before:dark:bg-dark-800',
					{ 'text-xs ': value, 'text-error-500': error, 'text-identity': focused },
					labelClass,
				]"
			>
				<span class="relative z-[2]">{{ label }}</span>
			</label>
		</div>
	</div>
	<div v-if="error" class="px-4 text-xs leading-4 text-error-500 mt-[2px]">{{ error }}</div>
</template>

<style scoped lang="scss">
label {
	&:before {
		@apply w-full h-[1px] absolute;
		content: "";
		z-index: 1;
		top: 12px;
		left: 50%;
		transform: translate(-50%, 0);
	}
}
</style>

<style lang="scss">
.p-dropdown-items-wrapper {
}
.p-dropdown-empty-message {
	@apply p-2;
}
.p-hidden-accessible {
	display: none;
}
</style>
